/**
 * LeadTypeCard Component
 *
 * Simplified delivery preferences card (no bids for MVP).
 * Shows activation + daily/monthly caps only.
 */

import { ToggleLeft, ToggleRight, Calendar, Hash } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { LeadPreference } from "@/contexts/AgentContext";

interface LeadTypeCardProps {
  preference: LeadPreference;
  onToggleActive: () => void;
  onUpdateDailyCap: (value: number) => void;
  onUpdateMonthlyCap: (value: number) => void;
}

const LeadTypeCard = ({
  preference,
  onToggleActive,
  onUpdateDailyCap,
  onUpdateMonthlyCap,
}: LeadTypeCardProps) => {
  return (
    <div
      className={cn(
        "rounded-xl border transition-all",
        preference.isActive
          ? "bg-card border-border"
          : "bg-muted/30 border-border/50"
      )}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <button
            onClick={onToggleActive}
            className="flex-shrink-0 mt-0.5"
            aria-label={preference.isActive ? "Deactivate" : "Activate"}
          >
            {preference.isActive ? (
              <ToggleRight className="w-7 h-7 text-accent" />
            ) : (
              <ToggleLeft className="w-7 h-7 text-muted-foreground" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={cn(
                "font-semibold",
                preference.isActive ? "text-foreground" : "text-muted-foreground"
              )}>
                {preference.leadTypeName}
              </h3>

              {!preference.isActive && (
                <Badge variant="secondary" className="text-xs">
                  Inactive
                </Badge>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div className="min-w-[100px]">
                <label className="text-xs text-muted-foreground mb-1 block">
                  Daily Cap
                </label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min={0}
                    value={preference.dailyCap || ""}
                    onChange={(e) => onUpdateDailyCap(Number(e.target.value))}
                    className="pl-8 h-9 w-20"
                    disabled={!preference.isActive}
                  />
                </div>
              </div>

              <div className="min-w-[100px]">
                <label className="text-xs text-muted-foreground mb-1 block">
                  Monthly Cap
                </label>
                <div className="relative">
                  <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min={0}
                    value={preference.monthlyCap || ""}
                    onChange={(e) => onUpdateMonthlyCap(Number(e.target.value))}
                    className="pl-8 h-9 w-20"
                    disabled={!preference.isActive}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadTypeCard;
